import { useEffect, useMemo, useState } from "react";
import CreateMeetingModal from "../../../components/modals/CreateMeetingModal";
import { updateMeetingOutcome } from "../../../services/meeting_service";
import { getMeetingOutcomeLabel, getMeetingStatusLabel, MEETING_OUTCOMES, MEETING_STATUSES } from "../../../models/meetingOutcomes";

function MeetingOutcomeEditor({ meeting, onSaved }) {
    const [status, setStatus] = useState(meeting.status || "scheduled");
    const [outcome, setOutcome] = useState(meeting.outcome || "");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const changed = status !== (meeting.status || "scheduled") || outcome !== (meeting.outcome || "");

    async function handleSave() {
        if (!changed || saving || (status === "attended" && !outcome)) return;
        setSaving(true);
        setError("");
        try {
            await updateMeetingOutcome(meeting.id, { status, outcome: status === "attended" ? outcome : "" });
            onSaved?.();
        } catch (err) {
            setError(err?.message || "Failed to update meeting");
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="contact-meeting-outcome-editor">
            <select value={status} onChange={(event) => {
                const nextStatus = event.target.value;
                setStatus(nextStatus);
                if (nextStatus !== "attended") setOutcome("");
            }} aria-label={`Status for ${meeting.subject || "meeting"}`}>
                {MEETING_STATUSES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
            {status === "attended" ? (
                <select value={outcome} onChange={(event) => setOutcome(event.target.value)} aria-label={`Outcome for ${meeting.subject || "meeting"}`}>
                    <option value="">Select result</option>
                    {MEETING_OUTCOMES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
            ) : null}
            <button type="button" onClick={handleSave} disabled={!changed || saving || (status === "attended" && !outcome)}>
                {saving ? "Saving..." : "Save"}
            </button>
            {error ? <span className="contact-meeting-outcome-error">{error}</span> : null}
        </div>
    );
}

function toDate(value) {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function dayKey(value) {
    const date = toDate(value);
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function formatDayLabel(date) {
    return date.toLocaleDateString([], {
        weekday: "short",
        month: "short",
        day: "numeric",
    });
}

function formatMeetingDate(dateValue) {
    const date = toDate(dateValue);
    if (!date) return "-";
    return date.toLocaleDateString([], {
        month: "short",
        day: "numeric",
    });
}

function formatMeetingDateTime(dateValue) {
    const date = toDate(dateValue);
    if (!date) return "-";
    return date.toLocaleString([], {
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
}

function formatMeetingDuration(startValue, endValue) {
    const start = toDate(startValue);
    const end = toDate(endValue);
    if (!start || !end) return "-";

    const durationMins = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
    if (durationMins < 60) return `${durationMins} min`;
    const hours = Math.floor(durationMins / 60);
    const mins = durationMins % 60;
    return mins === 0 ? `${hours} hr` : `${hours} hr ${mins} min`;
}

function monthGrid(anchorDate) {
    const monthStart = new Date(anchorDate.getFullYear(), anchorDate.getMonth(), 1);
    const monthEnd = new Date(anchorDate.getFullYear(), anchorDate.getMonth() + 1, 0);
    const startWeekday = monthStart.getDay();
    const totalDays = monthEnd.getDate();
    const cells = [];

    for (let i = 0; i < startWeekday; i += 1) {
        cells.push(null);
    }
    for (let day = 1; day <= totalDays; day += 1) {
        cells.push(new Date(anchorDate.getFullYear(), anchorDate.getMonth(), day));
    }
    while (cells.length % 7 !== 0) {
        cells.push(null);
    }
    return cells;
}

function MeetingsTab({ contactId, contact, meetings = [], meetingsLoading = false, meetingsError = "", onRefreshTabData, panelActionRequest }) {
    const [isCreateMeetingOpen, setIsCreateMeetingOpen] = useState(false);
    const [calendarMonth, setCalendarMonth] = useState(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), 1);
    });
    const [selectedDateKey, setSelectedDateKey] = useState(() => dayKey(new Date()));

    const byDay = useMemo(() => {
        const map = new Map();
        meetings.forEach((meeting) => {
            const key = dayKey(meeting.start_time);
            if (!key) return;
            const existing = map.get(key) || [];
            existing.push(meeting);
            map.set(key, existing);
        });
        return map;
    }, [meetings]);

    const calendarDays = useMemo(() => monthGrid(calendarMonth), [calendarMonth]);

    const selectedDayMeetings = useMemo(() => {
        const items = byDay.get(selectedDateKey) || [];
        return [...items].sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
    }, [byDay, selectedDateKey]);

    useEffect(() => {
        if (panelActionRequest?.type === "Meet") {
            setIsCreateMeetingOpen(true);
        }
    }, [panelActionRequest?.nonce]);

    return (
        <section className="tab-panel-container tab-panel-container--meetings" aria-label="Meetings tab">
            <div className="tab-panel-header">
                <div>
                    <h3>Meetings</h3>
                    <p className="tab-panel-subtitle">Schedule and review contact meeting history.</p>
                </div>
                <div className="tab-panel-actions">
                    <button
                        type="button"
                        className="tab-panel-btn tab-panel-btn--primary"
                        onClick={() => setIsCreateMeetingOpen(true)}
                    >
                        Schedule +
                    </button>
                </div>
            </div>

            <div className="contact-meetings-grid">
                <aside className="contact-meetings-calendar" aria-label="Meetings calendar">
                    <div className="contact-meetings-calendar-title">Calendar</div>
                    <div className="contact-meetings-month-switcher">
                        <button
                            type="button"
                            onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1))}
                        >
                            Prev
                        </button>
                        <span>
                            {calendarMonth.toLocaleDateString([], { month: "long", year: "numeric" })}
                        </span>
                        <button
                            type="button"
                            onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1))}
                        >
                            Next
                        </button>
                    </div>

                    <div className="contact-meetings-weekdays" aria-hidden="true">
                        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((weekday) => (
                            <span key={weekday}>{weekday}</span>
                        ))}
                    </div>

                    <div className="contact-meetings-day-grid">
                        {calendarDays.map((date, index) => {
                            if (!date) {
                                return <span key={`blank-${index}`} className="contact-meetings-day contact-meetings-day--blank" />;
                            }

                            const key = dayKey(date);
                            const count = (byDay.get(key) || []).length;
                            const isActive = selectedDateKey === key;

                            return (
                                <button
                                    key={key}
                                    type="button"
                                    className={`contact-meetings-day${isActive ? " is-active" : ""}${count > 0 ? " has-items" : ""}`}
                                    onClick={() => setSelectedDateKey(key)}
                                    title={count > 0 ? `${count} meeting${count === 1 ? "" : "s"}` : "No meetings"}
                                >
                                    <span>{date.getDate()}</span>
                                    {count > 0 ? <small>{count}</small> : null}
                                </button>
                            );
                        })}
                    </div>
                </aside>

                <div className="contact-meetings-history" aria-label="Meeting history">
                    <div className="contact-meetings-history-header">
                        <h4>History</h4>
                        <p>{formatDayLabel(toDate(selectedDateKey) || new Date())}</p>
                    </div>

                    {meetingsLoading ? (
                        <p className="contact-detail-tab-empty">Loading meetings...</p>
                    ) : meetingsError ? (
                        <p className="contact-detail-tab-empty">{meetingsError}</p>
                    ) : selectedDayMeetings.length === 0 ? (
                        <p className="contact-detail-tab-empty">No meetings on this date.</p>
                    ) : (
                        <div className="contact-meetings-history-list">
                            {selectedDayMeetings.map((meeting) => (
                                <article key={meeting.id} className="contact-meetings-history-card">
                                    <div className="contact-meetings-history-main">
                                        <h5>{meeting.subject || "Untitled meeting"}</h5>
                                        <p>{formatMeetingDateTime(meeting.start_time)}</p>
                                        <p>{formatMeetingDuration(meeting.start_time, meeting.end_time)}</p>
                                    </div>
                                    <div className="contact-meetings-history-actions">
                                        <span className="contact-meetings-status-pill">{getMeetingStatusLabel(meeting.status || "scheduled")}</span>
                                        {meeting.outcome ? <span className="contact-meetings-outcome">{getMeetingOutcomeLabel(meeting.outcome)}</span> : null}
                                        {meeting.teams_join_url ? (
                                            <a href={meeting.teams_join_url} target="_blank" rel="noreferrer">
                                                Join
                                            </a>
                                        ) : null}
                                        <MeetingOutcomeEditor meeting={meeting} onSaved={onRefreshTabData} />
                                    </div>
                                </article>
                            ))}
                        </div>
                    )}

                    {!meetingsLoading && !meetingsError && meetings.length > 0 ? (
                        <div className="contact-meetings-history-footnote">
                            {meetings.length} total meeting{meetings.length === 1 ? "" : "s"} for this contact
                        </div>
                    ) : null}
                </div>
            </div>

            {!meetingsLoading && !meetingsError && meetings.length > 0 ? (
                <div className="contact-meetings-upcoming">
                    <h4>Upcoming</h4>
                    <div className="contact-meetings-upcoming-list">
                        {meetings.slice(0, 3).map((meeting) => (
                            <div key={`upcoming-${meeting.id}`} className="contact-meetings-upcoming-item">
                                <span>{formatMeetingDate(meeting.start_time)}</span>
                                <strong>{meeting.subject || "Untitled"}</strong>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}

            {isCreateMeetingOpen ? (
                <CreateMeetingModal
                    isOpen={isCreateMeetingOpen}
                    onClose={() => setIsCreateMeetingOpen(false)}
                    onSuccess={() => {
                        setIsCreateMeetingOpen(false);
                        onRefreshTabData?.();
                    }}
                    contactId={Number(contactId)}
                    accountId={contact?.account_id || contact?.accountId || null}
                    accountName={contact?.account || contact?.company || ""}
                    contactEmail={contact?.email || ""}
                    contactName={contact?.name || `${contact?.first_name || ""} ${contact?.last_name || ""}`.trim()}
                />
            ) : null}
        </section>
    );
}

export default MeetingsTab;
